import { getAppBearer } from '@/lib/sdk-session-token'
import { resolveAttachmentUrl, type ResolveOptions } from './private-attachment'

/**
 * Attachment URLs served by our own backend, which sit behind `requireAuth`.
 *
 * The storage bucket's URLs are presigned and carry their own credentials in
 * the query string; ours carry none, so a bare fetch of one is a 401.
 */
export function needsAuth(url: string): boolean {
  return url.includes('/api/v1/attachments/')
}

/**
 * Headers for fetching an attachment's bytes.
 *
 * Both download helpers used to fetch with no Authorization header at all. On
 * a bearer-gated URL that is a 401, which sent them down their own fallback —
 * `window.open(url)` — and that tab has no token either, so it 401s as well.
 * The button appeared to do something and no file ever reached the disk.
 */
export function attachmentHeaders(url: string): HeadersInit | undefined {
  if (!needsAuth(url)) return undefined
  const token = getAppBearer()
  return token ? { Authorization: `Bearer ${token}` } : undefined
}

/**
 * Force a real download for an attachment.
 *
 * `<a href="…" download="name">` only works **same-origin**. Attachments live on
 * the storage host (`newinmeet-upload.instavc.ai`) while the app runs on
 * `app.instavc.ai`, so the browser silently ignores `download` and navigates to
 * the file instead — which for an image means leaving the chat to view it.
 *
 * Fetching the bytes and handing the anchor a same-origin `blob:` URL restores
 * the intended behaviour. Requires the object to be CORS-readable; if it isn't,
 * we fall back to opening it in a new tab so the user still gets to the file
 * rather than losing their place in the chat.
 */
export async function downloadAttachment(
  url: string,
  filename?: string | null,
  opts: ResolveOptions = {},
): Promise<void> {
  // A private chat file (VAPT 2026-10) downloads through a link signed now.
  // Refused, the plain URL is still tried below, and the CDN says why.
  const href = await resolveAttachmentUrl(url, opts).catch(() => url)
  try {
    const res = await fetch(href, { mode: 'cors', credentials: 'omit', headers: attachmentHeaders(href) })
    if (!res.ok) throw new Error(`download failed: ${res.status}`)
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    try {
      triggerAnchor(objectUrl, filename || inferName(url))
    } finally {
      // Revoke on the next tick — revoking synchronously can cancel the download.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
    }
  } catch {
    window.open(href, '_blank', 'noopener,noreferrer')
  }
}

function triggerAnchor(href: string, filename: string): void {
  const a = document.createElement('a')
  a.href = href
  a.download = filename
  a.rel = 'noopener'
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

function inferName(url: string): string {
  try {
    const path = new URL(url, window.location.origin).pathname
    const last = path.split('/').filter(Boolean).pop()
    return last || 'download'
  } catch {
    return 'download'
  }
}
